import React, { useEffect, useState, useMemo, useRef } from "react";
import { Loader2, RefreshCw, Download, ChevronDown, ChevronRight } from "lucide-react";
import { format, subWeeks } from "date-fns";
import * as XLSX from "xlsx-js-style";
import { toast } from "react-hot-toast";
import { resolveColorHex } from "../utils/teamColors";

// ── Types (mirror backend response) ─────────────────────────────────────────

interface IntervalData {
  intervalIndex: number;
  label: string;
  calls: number;
  pccsNeeded: number;
  isWorkingHour: boolean;
  isProjected: boolean;
}

interface Account {
  id: string;
  name: string;
  ahtMinutes: number;
  intervals: IntervalData[];
  projectionWindowStart: number;
  projectionWindowEnd: number;
  schedule: string;
  captureRate: number | null;
}

interface PCC {
  id: string;
  name: string;
  schedule: string;
  lunchDisplay: string; // e.g. "12:00–12:30" — empty if no lunch configured
  timezone: string;
  intervals: number[];
  lunchIntervalValues: Record<number, number>; // interval index → available fraction
}

interface Team {
  id: string;
  name: string;
  color: string;
  accounts: Account[];
  pccs: PCC[];
  captureRate: number | null;
}

interface Region {
  id: string;
  name: string;
  teams: Team[];
}

interface RegionOption {
  id: string;
  name: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number, decimals = 2): string {
  return n === 0 ? "—" : n.toFixed(decimals);
}

function fmtCaptureRate(rate: number | null): string {
  return rate === null ? "—" : `${(rate * 100).toFixed(1)}%`;
}

/** Convert "HH:mm" (24h) to AM/PM display: "6AM", "6:30AM", "1PM", "12:30PM" */
function toAmPm(label: string): string {
  const [hStr, mStr = "00"] = label.split(":");
  const h = parseInt(hStr, 10);
  const period = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return mStr === "00" ? `${h12}${period}` : `${h12}:${mStr}${period}`;
}

/** Convert time portions (HH:MM) within a schedule string to AM/PM, leaving day names intact.
 *  e.g. "Mon-Fri 08:00–17:00" → "Mon-Fri 8AM–5PM"
 *       "Mon 08:00–17:00, Tue 09:00–18:00" → "Mon 8AM–5PM, Tue 9AM–6PM"
 */
function scheduleToAmPm(schedule: string): string {
  if (!schedule) return schedule;
  return schedule.replace(/\b(\d{1,2}):(\d{2})\b/g, (_, hStr, mStr) => {
    const h = parseInt(hStr, 10);
    const period = h < 12 ? "AM" : "PM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return mStr === "00" ? `${h12}${period}` : `${h12}:${mStr}${period}`;
  });
}

function hexToRgba(hex: string, alpha: number): string {
  const clean = (hex || "6366f1").replace("#", "");
  if (clean.length < 6) return `rgba(99,102,241,${alpha})`;
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(99,102,241,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Mix hex color with white. fraction=0 → full color, fraction=1 → pure white. Returns opaque rgb(). */
function mixWithWhite(hex: string, fraction: number): string {
  const clean = (hex || "6366f1").replace("#", "");
  if (clean.length < 6) return `rgb(240,240,255)`;
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgb(240,240,255)`;
  return `rgb(${Math.round(r + (255 - r) * fraction)},${Math.round(g + (255 - g) * fraction)},${Math.round(b + (255 - b) * fraction)})`;
}

/** True when the background is light enough that white text would be illegible. */
function isLightBg(hex: string): boolean {
  const clean = (hex || "000000").replace("#", "");
  if (clean.length < 6) return false;
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.60;
}

const STICKY_NAME_W  = 220;
const STICKY_SCHED_W = 160;
const STICKY_AHT_W   = 72;
const STICKY_CR_W    = 84;
const STICKY_AHT_LEFT = STICKY_NAME_W + STICKY_SCHED_W;
const STICKY_CR_LEFT  = STICKY_NAME_W + STICKY_SCHED_W + STICKY_AHT_W;
const STICKY_TOTAL   = STICKY_NAME_W + STICKY_SCHED_W + STICKY_AHT_W + STICKY_CR_W;
const THEAD_H        = 37; // px — height of the column-header row (used for team-row sticky offset)

// ── MultiSelectDropdown ──────────────────────────────────────────────────────

interface MultiSelectOption { id: string; name: string; }

function MultiSelectDropdown({
  label,
  options,
  value,
  onChange,
  placeholder = "All",
}: {
  label: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const buttonLabel =
    value.length === 0
      ? placeholder
      : value.length === 1
      ? (options.find((o) => o.id === value[0])?.name ?? "1 selected")
      : `${value.length} selected`;

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 min-w-[140px] justify-between"
      >
        <span className={value.length === 0 ? "text-gray-400" : "text-gray-800 font-medium"}>
          {buttonLabel}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
      </button>
      {open && options.length > 0 && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg min-w-[180px] py-1 max-h-60 overflow-y-auto">
          {value.length > 0 && (
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50 font-medium border-b border-gray-100"
              onClick={() => { onChange([]); setOpen(false); }}
            >
              Clear selection
            </button>
          )}
          {options.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
            >
              <input
                type="checkbox"
                checked={value.includes(opt.id)}
                onChange={() => toggle(opt.id)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {opt.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Staffing summary card (company-wide / region-wide totals, front-and-center) ──

interface StaffingStat {
  avgDeficit: number;        // net avg PCCs short(+)/surplus(-) across all active interval×weekday slots
  peakDeficit: number;       // worst single interval×weekday slot (most understaffed)
  understaffedSlots: number; // count of slots with deficit > 0
  overstaffedSlots: number;  // count of slots with deficit < 0
  totalSlots: number;
}

function StaffingSummaryCard({
  label, stats, emphasize,
}: { label: string; stats: StaffingStat; emphasize?: boolean }) {
  const isShort   = stats.avgDeficit > 0.005;
  const isSurplus = stats.avgDeficit < -0.005;
  const tone = isShort ? "red" : isSurplus ? "green" : "gray";
  const toneClasses = {
    red:   { border: "border-red-200",   bg: "bg-red-50",   text: "text-red-700",   badge: "bg-red-100 text-red-700" },
    green: { border: "border-green-200", bg: "bg-green-50", text: "text-green-700", badge: "bg-green-100 text-green-700" },
    gray:  { border: "border-gray-200",  bg: "bg-gray-50",  text: "text-gray-600",  badge: "bg-gray-100 text-gray-600" },
  }[tone];

  return (
    <div className={`rounded-lg border ${toneClasses.border} ${toneClasses.bg} p-4 ${emphasize ? "ring-1 ring-indigo-300" : ""}`}>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-semibold uppercase tracking-wide ${emphasize ? "text-indigo-700" : "text-gray-500"}`}>
          {label}
        </span>
        {stats.totalSlots > 0 && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${toneClasses.badge}`}>
            {isShort ? "Understaffed" : isSurplus ? "Overstaffed" : "Balanced"}
          </span>
        )}
      </div>
      <div className={`mt-1.5 text-2xl font-bold ${toneClasses.text}`}>
        {stats.totalSlots === 0 ? "—" : `${stats.avgDeficit > 0 ? "+" : ""}${stats.avgDeficit.toFixed(2)}`}
      </div>
      <div className="text-xs text-gray-500">avg PCCs short / surplus per interval</div>
      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
        <span>
          Peak: <span className="font-semibold text-gray-700">
            {stats.totalSlots === 0 ? "—" : stats.peakDeficit > 0 ? `+${stats.peakDeficit.toFixed(2)}` : stats.peakDeficit.toFixed(2)}
          </span>
        </span>
        <span>{stats.understaffedSlots}/{stats.totalSlots} slots short</span>
      </div>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function HiringNeeds() {
  const today = format(new Date(), "yyyy-MM-dd");
  const eightWeeksAgo = format(subWeeks(new Date(), 8), "yyyy-MM-dd");

  // Rolling 8-week window, always — not manually editable.
  const startDate = eightWeeksAgo;
  const endDate   = today;
  const [intervalMinutes, setIntervalMinutes] = useState<30 | 60>(30);
  const [regionFilter, setRegionFilter]   = useState<string[]>([]);
  const [teamFilter, setTeamFilter]       = useState<string[]>([]);
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  // perDayData[dow] = Region[] for Mon(1)–Fri(5)
  const [perDayData, setPerDayData]       = useState<Record<number, Region[]>>({});
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [showProjections, setShowProjections] = useState(true);
  const [collapsedTeams, setCollapsedTeams]   = useState<Set<string>>(new Set());

  const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] as const; // Mon → Sun

  const toggleTeam = (teamId: string) =>
    setCollapsedTeams((prev) => {
      const next = new Set(prev);
      next.has(teamId) ? next.delete(teamId) : next.add(teamId);
      return next;
    });

  const authToken = localStorage.getItem("authToken");

  // ── Fetch region options ─────────────────────────────────────────────────
  useEffect(() => {
    fetch("/api/v1/capture-rates/regions-list", {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then((d) => setRegionOptions(d.data ?? []))
      .catch(() => {});
  }, []);

  // ── Fetch all 7 days in parallel ───────────────────────
  const fetchByDow = () => {
    setLoading(true);
    setError(null);
    setPerDayData({});

    const fetches = WEEKDAYS.map((dow) => {
      const params = new URLSearchParams({
        startDate, endDate,
        dayOfWeek: String(dow),
        intervalMinutes: String(intervalMinutes),
      });
      if (regionFilter.length) params.set("regionIds", regionFilter.join(","));
      return fetch(`/api/v1/hiring-needs?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then((r) => r.json())
        .then((d) => {
          if (!d.success) throw new Error(d.error || "Failed to load");
          return { dow, regions: (d.data ?? []) as Region[] };
        });
    });

    Promise.all(fetches)
      .then((results) => {
        const next: Record<number, Region[]> = {};
        for (const { dow, regions } of results) next[dow] = regions;
        setPerDayData(next);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  const fetchData = () => fetchByDow();

  useEffect(() => { fetchData(); }, [startDate, endDate, intervalMinutes, regionFilter]);

  // ── Reference structure (first available DOW — used for iterating regions/teams/accounts) ──
  const allRegions = useMemo<Region[]>(() => {
    for (const dow of WEEKDAYS) {
      const d = perDayData[dow];
      if (d?.length) return d;
    }
    return [];
  }, [perDayData]);

  // ── Helpers: look up a team's data for a specific DOW (by-dow mode) ──────
  const getTeamDay = (dow: number, regionId: string, teamId: string): Team | undefined =>
    perDayData[dow]?.find((r) => r.id === regionId)?.teams.find((t) => t.id === teamId);

  // Average account AHT across Mon-Fri
  const avgAht = (regionId: string, teamId: string, accountId: string): number => {
    const vals = WEEKDAYS
      .map((dow) => getTeamDay(dow, regionId, teamId)?.accounts.find((a) => a.id === accountId)?.ahtMinutes ?? 0)
      .filter((v) => v > 0);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  };

  // Average account interval calls + pccsNeeded across Mon-Fri
  const avgInterval = (regionId: string, teamId: string, accountId: string, idx: number) => {
    let calls = 0, pccs = 0, count = 0;
    for (const dow of WEEKDAYS) {
      const acc = getTeamDay(dow, regionId, teamId)?.accounts.find((a) => a.id === accountId);
      if (!acc) continue;
      calls += acc.intervals[idx]?.calls ?? 0;
      pccs  += acc.intervals[idx]?.pccsNeeded ?? 0;
      count++;
    }
    return count > 0 ? { calls: calls / count, pccsNeeded: pccs / count } : { calls: 0, pccsNeeded: 0 };
  };

  // True if ANY weekday's data for this account/interval is projected (from Weekly Estimates)
  const isIntervalProjected = (regionId: string, teamId: string, accountId: string, idx: number): boolean => {
    for (const dow of WEEKDAYS) {
      const acc = getTeamDay(dow, regionId, teamId)?.accounts.find((a) => a.id === accountId);
      if (acc?.intervals[idx]?.isProjected) return true;
    }
    return false;
  };

  // Team staffing deficit for a specific DOW and interval (by-dow mode)
  const teamDeficit = (dow: number, regionId: string, refTeam: Team, idx: number): number => {
    const dayTeam = getTeamDay(dow, regionId, refTeam.id);
    if (!dayTeam) return 0;
    const needed    = dayTeam.accounts.reduce((s, acc) => s + (acc.intervals[idx]?.pccsNeeded ?? 0), 0);
    const available = refTeam.pccs.reduce((s, pcc) => s + (pcc.intervals[idx] ?? 0), 0);
    return Math.round((needed - available) * 100) / 100;
  };

  // Region-wide deficit for a specific DOW and interval (across all visible teams)
  const regionDeficit = (dow: number, region: Region, idx: number): number => {
    let total = 0;
    for (const team of region.teams) {
      if (teamFilter.length > 0 && !teamFilter.includes(team.id)) continue;
      total += teamDeficit(dow, region.id, team, idx);
    }
    return Math.round(total * 100) / 100;
  };

  // Overall deficit for a specific DOW and interval (across all regions)
  const overallDeficit = (dow: number, idx: number): number => {
    let total = 0;
    for (const region of allRegions) total += regionDeficit(dow, region, idx);
    return Math.round(total * 100) / 100;
  };

  // ── Interval labels ───────────────────────────────────────────────────────
  const intervalLabels = useMemo<string[]>(() => {
    for (const region of allRegions) {
      for (const team of region.teams) {
        const ivSrc = team.accounts[0]?.intervals;
        if (ivSrc?.length) return ivSrc.map((iv) => iv.label);
      }
    }
    const count = (24 * 60) / intervalMinutes;
    return Array.from({ length: count }, (_, i) => {
      const m = i * intervalMinutes;
      return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
    });
  }, [allRegions, intervalMinutes]);

  // ── Team options from reference structure ─────────────────────────────────
  const teamOptions = useMemo<MultiSelectOption[]>(() => {
    const seen = new Set<string>();
    const result: MultiSelectOption[] = [];
    for (const region of allRegions)
      for (const team of region.teams)
        if (!seen.has(team.id)) { seen.add(team.id); result.push({ id: team.id, name: team.name }); }
    return result;
  }, [allRegions]);

  // ── Active interval set ────────────────────────────────────────────────────
  const activeIntervalSet = useMemo(() => {
    const set = new Set<number>();
    for (const dow of WEEKDAYS) {
      for (const region of (perDayData[dow] ?? []))
        for (const team of region.teams) {
          if (teamFilter.length > 0 && !teamFilter.includes(team.id)) continue;
          for (const account of team.accounts)
            for (const iv of account.intervals)
              if (iv.calls > 0 && (!iv.isProjected || showProjections)) set.add(iv.intervalIndex);
        }
    }
    return set;
  }, [perDayData, showProjections, teamFilter]);

  // ── Company-wide / region-wide staffing summary stats ──────────────────────
  const computeStaffingStats = (deficitFn: (dow: number, idx: number) => number): StaffingStat => {
    const activeIndices = [...activeIntervalSet];
    let sum = 0, peak = -Infinity, understaffed = 0, overstaffed = 0, total = 0;
    for (const dow of WEEKDAYS) {
      for (const idx of activeIndices) {
        const d = deficitFn(dow, idx);
        sum += d;
        if (d > peak) peak = d;
        if (d > 0) understaffed++;
        else if (d < 0) overstaffed++;
        total++;
      }
    }
    return {
      avgDeficit: total > 0 ? Math.round((sum / total) * 100) / 100 : 0,
      peakDeficit: total > 0 ? Math.round(peak * 100) / 100 : 0,
      understaffedSlots: understaffed,
      overstaffedSlots: overstaffed,
      totalSlots: total,
    };
  };

  const companyStats = useMemo<StaffingStat>(
    () => computeStaffingStats((dow, idx) => overallDeficit(dow, idx)),
    [perDayData, teamFilter, activeIntervalSet]
  );

  const regionStatsList = useMemo<{ region: Region; stats: StaffingStat }[]>(
    () => allRegions.map((region) => ({
      region,
      stats: computeStaffingStats((dow, idx) => regionDeficit(dow, region, idx)),
    })),
    [allRegions, teamFilter, activeIntervalSet]
  );

  const colW = intervalMinutes === 30 ? 58 : 72;

  // ── Excel export ─────────────────────────────────────────────────────────
  const downloadExcel = () => {
    if (allRegions.length === 0 || activeIntervalSet.size === 0) {
      toast.error("No data to export");
      return;
    }

    const activeIndices = [...activeIntervalSet].sort((a, b) => a - b);
    const activeLabels  = activeIndices.map((i) => toAmPm(intervalLabels[i]));
    const ALL_COLS      = ["Name", "Schedule", "AHT (min)", ...activeLabels];
    const NUM_COLS      = ALL_COLS.length;

    const toArgb = (raw: string | null | undefined): string =>
      "FF" + resolveColorHex(raw).replace("#", "").toUpperCase();
    const isDark = (argb: string): boolean => {
      const hex = argb.slice(2);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55;
    };
    const fmtDeficit = (d: number) => d === 0 ? "" : d > 0 ? `+${d.toFixed(2)}` : d.toFixed(2);

    type RowType = "title" | "regionHeader" | "teamHeader" | "colHeader" | "account" | "pcc" | "staffing" | "regionSummary" | "overallSummary" | "spacer";
    const aoa: any[][] = [];
    const merges: XLSX.Range[] = [];
    const rowMeta: { type: RowType; teamArgb?: string; oddRow?: boolean; deficits?: number[] }[] = [];

    const pushMergedRow = (text: string, type: RowType, teamArgb?: string) => {
      aoa.push([text, ...Array(NUM_COLS - 1).fill("")]);
      merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: NUM_COLS - 1 } });
      rowMeta.push({ type, teamArgb });
    };

    const pushStaffingRow = (label: string, deficits: number[], type: RowType, teamArgb?: string) => {
      if (deficits.every((d) => d === 0)) return;
      aoa.push([label, "", "", ...deficits.map(fmtDeficit)]);
      rowMeta.push({ type, teamArgb, deficits });
    };

    const rangeStr = startDate === endDate ? startDate : `${startDate} to ${endDate}`;
    pushMergedRow(`Hiring Needs — Daily Breakdown — ${rangeStr}`, "title");

    for (const region of allRegions) {
      pushMergedRow(`  ${region.name.toUpperCase()}`, "regionHeader");

      for (const team of region.teams) {
        if (teamFilter.length > 0 && !teamFilter.includes(team.id)) continue;
        const teamArgb = toArgb(team.color);

        pushMergedRow(`  ${team.name}`, "teamHeader", teamArgb);
        aoa.push([...ALL_COLS]);
        rowMeta.push({ type: "colHeader", teamArgb });

        // Account rows (weekday average)
        team.accounts.forEach((account, i) => {
          const ahtAvg = avgAht(region.id, team.id, account.id);
          const row: any[] = [
            `Account: ${account.name}`, "—",
            ahtAvg > 0 ? ahtAvg.toFixed(1) : "—",
          ];
          for (const idx of activeIndices) {
            const { calls, pccsNeeded } = avgInterval(region.id, team.id, account.id, idx);
            if (calls === 0) { row.push(""); continue; }
            row.push(`${pccsNeeded.toFixed(2)}\n(${Number.isInteger(calls) ? calls : calls.toFixed(1)} calls)`);
          }
          aoa.push(row);
          rowMeta.push({ type: "account", teamArgb, oddRow: i % 2 !== 0 });
        });

        // PCC rows
        team.pccs.forEach((pcc, i) => {
          const row: any[] = [`PCC: ${pcc.name}`, pcc.schedule ? scheduleToAmPm(pcc.schedule) : "—", "—"];
          for (const idx of activeIndices) {
            const lunchFraction = pcc.lunchIntervalValues?.[idx];
            if (lunchFraction !== undefined) {
              row.push(lunchFraction > 0 ? lunchFraction.toFixed(2) : "0");
            } else {
              row.push((pcc.intervals[idx] ?? 0) > 0 ? (pcc.intervals[idx] ?? 0).toFixed(2) : "");
            }
          }
          aoa.push(row);
          rowMeta.push({ type: "pcc", teamArgb, oddRow: (team.accounts.length + i) % 2 !== 0 });
        });

        // Per-day staffing rows
        for (const dow of WEEKDAYS) {
          const deficits = activeIndices.map((idx) => teamDeficit(dow, region.id, team, idx));
          pushStaffingRow(`Staffing Status — ${DOW_LABELS[dow]}`, deficits, "staffing", teamArgb);
        }

        aoa.push(Array(NUM_COLS).fill(""));
        rowMeta.push({ type: "spacer" });
      }

      // Per-region per-day summary
      for (const dow of WEEKDAYS) {
        const deficits = activeIndices.map((idx) => regionDeficit(dow, region, idx));
        pushStaffingRow(`${region.name} Total — ${DOW_LABELS[dow]}`, deficits, "regionSummary");
      }
      aoa.push(Array(NUM_COLS).fill(""));
      rowMeta.push({ type: "spacer" });
    }

    // Overall per-day summary (only if multiple regions)
    if (allRegions.length > 1) {
      pushMergedRow("  OVERALL SUMMARY", "regionHeader");
      for (const dow of WEEKDAYS) {
        const deficits = activeIndices.map((idx) => overallDeficit(dow, idx));
        pushStaffingRow(`Overall — ${DOW_LABELS[dow]}`, deficits, "overallSummary");
      }
    }

    // ── Build worksheet ──────────────────────────────────────────────────
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;
    ws["!cols"]   = [{ wch: 32 }, { wch: 20 }, { wch: 10 }, ...activeIndices.map(() => ({ wch: 10 }))];
    ws["!rows"]   = rowMeta.map((m) => {
      if (m.type === "title")          return { hpt: 26 };
      if (m.type === "regionHeader")   return { hpt: 20 };
      if (m.type === "teamHeader")     return { hpt: 18 };
      if (m.type === "colHeader")      return { hpt: 16 };
      if (m.type === "staffing")       return { hpt: 18 };
      if (m.type === "regionSummary")  return { hpt: 20 };
      if (m.type === "overallSummary") return { hpt: 20 };
      if (m.type === "spacer")         return { hpt: 6 };
      return { hpt: 22 };
    });

    const C = { h: "center" as const, v: "center" as const };
    const L = { h: "left"   as const, v: "center" as const };
    const W = { h: "center" as const, v: "center" as const, wrapText: true };

    // Border helpers
    const bThin  = (rgb: string) => ({ style: "thin"  as const, color: { rgb } });
    const bThick = (rgb: string) => ({ style: "thick" as const, color: { rgb } });
    const allBorders = (rgb: string) => ({ top: bThin(rgb), bottom: bThin(rgb), left: bThin(rgb), right: bThin(rgb) });

    for (let r = 0; r < rowMeta.length; r++) {
      const meta  = rowMeta[r];
      const argb  = meta.teamArgb ?? "FF6366F1";
      const dark  = isDark(argb);
      const hexBg = argb.slice(2);

      for (let c = 0; c < NUM_COLS; c++) {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) ws[ref] = { v: "", t: "s" };

        switch (meta.type) {
          case "title":
            ws[ref].s = {
              fill: { fgColor: { rgb: "1E3A5F" }, patternType: "solid" },
              font: { color: { rgb: "FFFFFF" }, bold: true, sz: 14, name: "Calibri" },
              alignment: L,
              border: allBorders("2D4A6F"),
            };
            break;
          case "regionHeader":
            ws[ref].s = {
              fill: { fgColor: { rgb: "334155" }, patternType: "solid" },
              font: { color: { rgb: "FFFFFF" }, bold: true, sz: 11, name: "Calibri" },
              alignment: L,
              border: allBorders("475569"),
            };
            break;
          case "teamHeader":
            ws[ref].s = {
              fill: { fgColor: { rgb: hexBg }, patternType: "solid" },
              font: { color: { rgb: dark ? "FFFFFF" : "1A1A1A" }, bold: true, sz: 11, name: "Calibri" },
              alignment: L,
              border: allBorders(dark ? "FFFFFF" : "00000022"),
            };
            break;
          case "colHeader":
            ws[ref].s = {
              fill: { fgColor: { rgb: "2D3748" }, patternType: "solid" },
              font: { color: { rgb: "FFFFFF" }, bold: true, sz: 9, name: "Calibri" },
              alignment: C,
              border: allBorders("4A5568"),
            };
            break;
          case "account":
          case "pcc": {
            const bg = meta.oddRow ? "EAF0F8" : "FFFFFF";
            const fc = meta.type === "pcc" && c >= 3 ? "4338CA" : "1A1A1A";
            ws[ref].s = {
              fill: { fgColor: { rgb: bg }, patternType: "solid" },
              font: { color: { rgb: fc }, sz: 9, name: "Calibri", bold: c === 0 },
              alignment: c === 0 ? L : c >= 3 ? W : C,
              border: {
                top:    bThin("D1D5DB"),
                bottom: bThin("D1D5DB"),
                right:  bThin("D1D5DB"),
                left:   c === 0 ? bThick(hexBg) : bThin("D1D5DB"),
              },
            };
            break;
          }
          case "staffing":
          case "regionSummary":
          case "overallSummary": {
            const isRegion  = meta.type === "regionSummary";
            const isOverall = meta.type === "overallSummary";
            const labelBg   = isOverall ? "1E3A5F" : isRegion ? "1E293B" : "F9FAFB";
            const labelFg   = isOverall || isRegion ? "FFFFFF" : "374151";
            const darkRow   = isRegion || isOverall;
            const borderClr = darkRow ? "3A4A5C" : "D1D5DB";
            if (c < 3) {
              ws[ref].s = {
                fill: { fgColor: { rgb: labelBg }, patternType: "solid" },
                font: { color: { rgb: labelFg }, bold: true, sz: 9, name: "Calibri" },
                alignment: c === 0 ? L : C,
                border: {
                  top:    bThin(borderClr),
                  bottom: bThin(borderClr),
                  right:  bThin(borderClr),
                  left:   c === 0 && meta.type === "staffing" ? bThick(hexBg) : bThin(borderClr),
                },
              };
            } else {
              const d  = meta.deficits?.[c - 3] ?? 0;
              const bg = d > 1 ? "FEE2E2" : d > 0 ? "FEF9C3" : d < 0 ? "DCFCE7" : darkRow ? labelBg : "F9FAFB";
              const fg = d > 1 ? "B91C1C" : d > 0 ? "92400E" : d < 0 ? "166534" : darkRow ? "FFFFFF" : "374151";
              const cellBorderClr = d > 1 ? "FCA5A5" : d > 0 ? "FDE047" : d < 0 ? "86EFAC" : borderClr;
              ws[ref].s = {
                fill: { fgColor: { rgb: bg }, patternType: "solid" },
                font: { color: { rgb: fg }, bold: true, sz: 9, name: "Calibri" },
                alignment: C,
                border: allBorders(cellBorderClr),
              };
            }
            break;
          }
          default:
            ws[ref].s = { fill: { fgColor: { rgb: "F8FAFC" }, patternType: "solid" }, border: allBorders("E2E8F0") };
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Hiring Needs");
    XLSX.writeFile(wb, `hiring-needs_${startDate}_${endDate}.xlsx`);
    toast.success("Excel file downloaded");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hiring Needs</h1>
          <p className="mt-1 text-sm text-gray-500">
            PCCs needed vs utilization per interval
          </p>
        </div>
        <button
          onClick={downloadExcel}
          disabled={loading || allRegions.length === 0}
          className="inline-flex items-center gap-2 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-4 w-4" />
          Export Excel
        </button>
      </div>

      {/* Front-and-center staffing summary — company-wide + region-wide totals */}
      {!loading && !error && allRegions.length > 0 && (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {allRegions.length > 1 && (
            <StaffingSummaryCard label="Company-Wide" stats={companyStats} emphasize />
          )}
          {regionStatsList.map(({ region, stats }) => (
            <StaffingSummaryCard key={region.id} label={region.name} stats={stats} />
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-gray-200 bg-white p-4">
        {/* Interval toggle */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Interval</label>
          <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm">
            {([30, 60] as const).map((v) => (
              <button
                key={v}
                onClick={() => setIntervalMinutes(v)}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  intervalMinutes === v
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {v === 30 ? "30 min" : "1 hr"}
              </button>
            ))}
          </div>
        </div>

        {/* Region filter */}
        {regionOptions.length > 0 && (
          <MultiSelectDropdown
            label="Region"
            options={regionOptions}
            value={regionFilter}
            onChange={(v) => { setRegionFilter(v); setTeamFilter([]); }}
            placeholder="All Regions"
          />
        )}

        {/* Team filter */}
        {teamOptions.length > 0 && (
          <MultiSelectDropdown
            label="Team"
            options={teamOptions}
            value={teamFilter}
            onChange={setTeamFilter}
            placeholder="All Teams"
          />
        )}

        {/* Projection toggle */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Projections</label>
          <button
            onClick={() => setShowProjections((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              showProjections
                ? "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
            }`}
            title={showProjections ? "Hide projected call estimates" : "Show projected call estimates (from Weekly Estimates)"}
          >
            {showProjections ? "Projections ON" : "Projections OFF"}
          </button>
        </div>

        {/* Refresh */}
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* States */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500">Loading hiring needs…</span>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && allRegions.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white py-16 text-center text-sm text-gray-500">
          No data found for the selected date and filters.
        </div>
      )}

      {/* Table */}
      {!loading && !error && allRegions.length > 0 && (
        <div
          className="rounded-lg border border-gray-200 bg-white overflow-auto"
          style={{ height: "calc(100vh - 260px)" }}
        >
            <table className="border-collapse text-xs" style={{ minWidth: STICKY_TOTAL + intervalLabels.length * colW }}>
              {/* ── Column header ── */}
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {/* z-40: sticky BOTH top and left — must sit above everything */}
                  <th
                    className="sticky left-0 top-0 z-40 bg-gray-50 border-r border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
                    style={{ minWidth: STICKY_NAME_W, width: STICKY_NAME_W }}
                  >
                    Name
                  </th>
                  <th
                    className="sticky top-0 z-40 bg-gray-50 border-r border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
                    style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, width: STICKY_SCHED_W }}
                  >
                    Schedule
                  </th>
                  <th
                    className="sticky top-0 z-40 bg-gray-50 border-r border-gray-300 px-3 py-2 text-center font-semibold text-gray-600 whitespace-nowrap"
                    style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W, width: STICKY_AHT_W }}
                  >
                    AHT
                  </th>
                  <th
                    className="sticky top-0 z-40 bg-gray-50 border-r border-gray-300 px-3 py-2 text-center font-semibold text-gray-600 whitespace-nowrap"
                    style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W, width: STICKY_CR_W }}
                    title="Trailing capture rate over the selected date range (same formula as the Capture Rates page)"
                  >
                    Capture Rate
                  </th>
                  {/* z-30: sticky top only — interval label columns */}
                  {intervalLabels.map((label, idx) => (
                    <th
                      key={label}
                      className={`sticky top-0 z-30 bg-gray-50 px-0 py-2 text-center font-medium whitespace-nowrap transition-colors ${
                        activeIntervalSet.has(idx)
                          ? "text-gray-600"
                          : "text-gray-300"
                      }`}
                      style={{ minWidth: colW, width: colW, fontSize: intervalMinutes === 30 ? 10 : 11 }}
                    >
                      {toAmPm(label)}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {allRegions.map((region) => (
                  <React.Fragment key={`region-${region.id}`}>
                    {/* ── Region header ── */}
                    <tr>
                      <td
                        className="sticky left-0 z-20 px-4 py-1.5 font-bold text-white text-xs tracking-wide uppercase bg-slate-700"
                        style={{ minWidth: STICKY_NAME_W, width: STICKY_NAME_W }}
                      >
                        {region.name}
                      </td>
                      <td className="sticky z-20 bg-slate-700" style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W }} />
                      <td className="sticky z-20 bg-slate-700" style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W }} />
                      <td className="sticky z-20 bg-slate-700" style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W }} />
                      {intervalLabels.map((_, idx) => (
                        <td key={idx} className="bg-slate-700" style={{ minWidth: colW }} />
                      ))}
                    </tr>

                    {region.teams.map((team) => {
                      if (teamFilter.length > 0 && !teamFilter.includes(team.id)) return null;
                      const teamBg      = resolveColorHex(team.color);
                      const teamBgFaint  = hexToRgba(teamBg, 0.12);
                      const teamBgSolid  = mixWithWhite(teamBg, 0.82); // opaque light tint — no bleed-through when sticky
                      const teamTextColor = isLightBg(teamBg) ? "#1a1a1a" : "#ffffff";
                      const teamBorderColor = isLightBg(teamBg) ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.20)";
                      const isCollapsed = collapsedTeams.has(team.id);

                      return (
                        <React.Fragment key={`team-${team.id}`}>
                          {/* ── Team header (clickable to collapse, sticky below column header) ── */}
                          <tr
                            className="cursor-pointer select-none"
                            onClick={() => toggleTeam(team.id)}
                            title={isCollapsed ? "Click to expand" : "Click to collapse"}
                          >
                            <td
                              className="sticky left-0 px-4 py-1.5 font-bold text-xs tracking-wide"
                              style={{
                                minWidth: STICKY_NAME_W, width: STICKY_NAME_W,
                                top: THEAD_H, backgroundColor: teamBg, zIndex: 35,
                                color: teamTextColor,
                                borderBottom: `1px solid ${teamBorderColor}`,
                              }}
                            >
                              <span className="inline-flex items-center gap-1.5">
                                {isCollapsed
                                  ? <ChevronRight className="h-3 w-3 flex-shrink-0 opacity-80" />
                                  : <ChevronDown  className="h-3 w-3 flex-shrink-0 opacity-80" />
                                }
                                {team.name}
                              </span>
                            </td>
                            <td
                              className="sticky"
                              style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, top: THEAD_H, backgroundColor: teamBg, zIndex: 35, borderBottom: `1px solid ${teamBorderColor}` }}
                            />
                            <td
                              className="sticky"
                              style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W, top: THEAD_H, backgroundColor: teamBg, zIndex: 35, borderBottom: `1px solid ${teamBorderColor}` }}
                            />
                            <td
                              className="sticky text-center font-bold text-xs"
                              style={{
                                left: STICKY_CR_LEFT, minWidth: STICKY_CR_W, top: THEAD_H,
                                backgroundColor: teamBg, zIndex: 35, color: teamTextColor,
                                borderBottom: `1px solid ${teamBorderColor}`,
                              }}
                              title="Blended trailing capture rate across all accounts on this team"
                            >
                              {fmtCaptureRate(team.captureRate)}
                            </td>
                            {/* Opaque solid tint — prevents rows scrolling underneath from bleeding through */}
                            {intervalLabels.map((_, idx) => (
                              <td
                                key={idx}
                                className="sticky z-20"
                                style={{ minWidth: colW, top: THEAD_H, backgroundColor: teamBgSolid, borderBottom: `1px solid ${teamBorderColor}` }}
                              />
                            ))}
                          </tr>

                          {/* ════════════════════════════════════════════════
                              Averaged account + PCC rows (by day of week)
                          ════════════════════════════════════════════════ */}
                          <>
                              {/* Account rows (weekday-average PCCs Needed) */}
                              {!isCollapsed && team.accounts.map((account) => {
                                const ahtAvg = avgAht(region.id, team.id, account.id);
                                return (
                                  <tr key={`account-${account.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td
                                      className="sticky left-0 z-10 border-r border-gray-200 px-3 py-1.5 font-medium text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis bg-white"
                                      style={{ minWidth: STICKY_NAME_W, maxWidth: STICKY_NAME_W, borderLeft: `3px solid ${teamBg}` }}
                                      title={account.name}
                                    >
                                      <span className="text-gray-400 mr-1">Account:</span>
                                      {account.name}
                                    </td>
                                    <td
                                      className="sticky z-10 border-r border-gray-200 px-3 py-1.5 bg-white"
                                      style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, maxWidth: STICKY_SCHED_W }}
                                      title={scheduleToAmPm(account.schedule) || "No schedule configured"}
                                    >
                                      {account.schedule ? (
                                        <span className="text-xs text-blue-600 whitespace-pre-line leading-tight">
                                          {scheduleToAmPm(account.schedule)}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-300 italic">No schedule</span>
                                      )}
                                    </td>
                                    <td
                                      className="sticky z-10 border-r border-gray-300 px-3 py-1.5 text-center font-mono text-gray-700 bg-white"
                                      style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W }}
                                    >
                                      {ahtAvg > 0 ? `${ahtAvg.toFixed(1)}m` : "—"}
                                    </td>
                                    <td
                                      className="sticky z-10 border-r border-gray-300 px-3 py-1.5 text-center font-mono bg-white"
                                      style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W, color: account.captureRate === null ? "#9ca3af" : "#1e293b" }}
                                      title="Trailing capture rate over the selected date range (same formula as the Capture Rates page)"
                                    >
                                      {fmtCaptureRate(account.captureRate)}
                                    </td>
                                    {intervalLabels.map((_, idx) => {
                                      const { calls, pccsNeeded } = avgInterval(region.id, team.id, account.id, idx);
                                      const projected = isIntervalProjected(region.id, team.id, account.id, idx);
                                      if (projected && !showProjections)
                                        return <td key={idx} style={{ minWidth: colW, backgroundColor: "#F9FAFB" }} />;
                                      return (
                                        <td key={idx} className="py-1.5 text-center font-mono transition-colors"
                                          style={{
                                            minWidth: colW,
                                            backgroundColor: calls > 0 ? (projected ? "rgba(148,163,184,0.12)" : teamBgFaint) : "#F9FAFB",
                                            color: calls > 0 ? (projected ? "#64748b" : "#1e293b") : "#9ca3af",
                                            opacity: projected ? 0.85 : 1,
                                            outline: projected ? "1px dashed #94a3b8" : undefined,
                                            outlineOffset: "-1px",
                                          }}
                                          title={projected ? "Projected (from Weekly Estimates)" : undefined}
                                        >
                                          {calls > 0 ? (
                                            <div className="flex flex-col items-center leading-tight gap-px">
                                              <span className="font-semibold">{projected ? `~${fmt(pccsNeeded)}` : fmt(pccsNeeded)}</span>
                                              <span style={{ fontSize: 10, color: "#6366f1", opacity: projected ? 0.7 : 0.85, fontWeight: 500 }}>
                                                {projected ? "~" : ""}{Number.isInteger(calls) ? calls : calls.toFixed(1)} calls
                                              </span>
                                            </div>
                                          ) : ""}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}

                              {/* PCC rows */}
                              {!isCollapsed && team.pccs.map((pcc) => (
                                <tr key={`pcc-${pcc.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="sticky left-0 z-10 border-r border-gray-200 px-3 py-1.5 text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis bg-white"
                                    style={{ minWidth: STICKY_NAME_W, maxWidth: STICKY_NAME_W, borderLeft: `3px solid ${teamBg}` }} title={pcc.name}>
                                    <span className="text-indigo-400 mr-1">PCC:</span>{pcc.name}
                                  </td>
                                  <td className="sticky z-10 border-r border-gray-200 px-3 py-1.5 bg-white"
                                    style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, maxWidth: STICKY_SCHED_W }}
                                    title={[scheduleToAmPm(pcc.schedule), pcc.lunchDisplay ? `Lunch: ${scheduleToAmPm(pcc.lunchDisplay)}` : ""].filter(Boolean).join(" · ")}>
                                    <div className="flex flex-col gap-0.5 leading-tight">
                                      <span className="text-xs text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">{scheduleToAmPm(pcc.schedule)}</span>
                                      {pcc.lunchDisplay && <span className="text-xs text-amber-500 whitespace-nowrap">🍽 {scheduleToAmPm(pcc.lunchDisplay)}</span>}
                                    </div>
                                  </td>
                                  <td className="sticky z-10 border-r border-gray-300 px-3 py-1.5 text-center text-gray-400 bg-white"
                                    style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W }}>—</td>
                                  <td className="sticky z-10 border-r border-gray-300 px-3 py-1.5 text-center text-gray-400 bg-white"
                                    style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W }}>—</td>
                                  {pcc.intervals.map((util, idx) => {
                                    const lunchFraction = pcc.lunchIntervalValues?.[idx];
                                    if (lunchFraction !== undefined) {
                                      return (
                                        <td key={idx} className="py-1.5 text-center font-semibold"
                                          style={{ minWidth: colW, backgroundColor: "#FEF9C3", color: "#92400E" }} title="Lunch break">
                                          <span style={{ fontSize: 11, fontWeight: 600 }}>{lunchFraction > 0 ? lunchFraction.toFixed(2) : "0"}</span>
                                        </td>
                                      );
                                    }
                                    return (
                                      <td key={idx} className="py-1.5 text-center font-mono transition-colors"
                                        style={{ minWidth: colW, backgroundColor: util > 0 ? hexToRgba(teamBg, 0.07) : "#F9FAFB", color: util > 0 ? "#4338ca" : "#d1d5db" }}>
                                        {util > 0 ? util.toFixed(2) : ""}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}

                              {/* Staffing Status — one row per weekday */}
                              {!isCollapsed && WEEKDAYS.map((dow) => {
                                const deficits = intervalLabels.map((_, idx) => teamDeficit(dow, region.id, team, idx));
                                if (deficits.every((d) => d === 0)) return null;
                                return (
                                  <tr key={`staffing-${team.id}-${dow}`} className="border-b-2 border-gray-300">
                                    <td className="sticky left-0 z-10 border-r border-gray-200 px-3 py-1.5 font-semibold whitespace-nowrap bg-gray-50"
                                      style={{ minWidth: STICKY_NAME_W, maxWidth: STICKY_NAME_W, borderLeft: `3px solid ${teamBg}`, color: "#374151" }}>
                                      Staffing Status — {DOW_LABELS[dow]}
                                    </td>
                                    <td className="sticky z-10 border-r border-gray-200 bg-gray-50" style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W }} />
                                    <td className="sticky z-10 border-r border-gray-300 bg-gray-50" style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W }} />
                                    <td className="sticky z-10 border-r border-gray-300 bg-gray-50" style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W }} />
                                    {deficits.map((d, idx) => {
                                      if (d === 0) return <td key={idx} style={{ minWidth: colW, backgroundColor: "#F9FAFB" }} />;
                                      const bgColor   = d > 1 ? "#FEE2E2" : d >= 0 ? "#FEF9C3" : "#DCFCE7";
                                      const textColor = d > 1 ? "#B91C1C" : d >= 0 ? "#92400E" : "#166534";
                                      return (
                                        <td key={idx} className="py-1.5 text-center font-mono font-semibold"
                                          style={{ minWidth: colW, backgroundColor: bgColor, color: textColor }}>
                                          {d > 0 ? `+${d.toFixed(2)}` : d.toFixed(2)}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </>

                          {/* ── Spacer between teams ── */}
                          <tr>
                            <td colSpan={4 + intervalLabels.length} className="h-1 bg-gray-100" />
                          </tr>
                        </React.Fragment>
                      );
                    })}

                    {/* ── Region summary ── */}
                    {(() => {
                      const visibleTeamCount = region.teams.filter(
                        (t) => teamFilter.length === 0 || teamFilter.includes(t.id)
                      ).length;
                      if (visibleTeamCount < 2) return null;
                      return (
                        <>
                          {/* Section divider */}
                          <tr>
                            <td
                              className="sticky left-0 z-10 px-4 py-1 text-[10px] font-bold uppercase tracking-widest border-t-2 border-slate-400"
                              style={{ minWidth: STICKY_NAME_W, backgroundColor: "#E2E8F0", color: "#475569" }}
                            >
                              {region.name} — Daily Totals
                            </td>
                            <td className="sticky z-10 border-t-2 border-slate-400" style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, backgroundColor: "#E2E8F0" }} />
                            <td className="sticky z-10 border-t-2 border-slate-400" style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W, backgroundColor: "#E2E8F0" }} />
                            <td className="sticky z-10 border-t-2 border-slate-400" style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W, backgroundColor: "#E2E8F0" }} />
                            {intervalLabels.map((_, idx) => (
                              <td key={idx} className="border-t-2 border-slate-400" style={{ minWidth: colW, backgroundColor: "#E2E8F0" }} />
                            ))}
                          </tr>

                          {/* Per-day totals rows */}
                          {WEEKDAYS.map((dow) => {
                            const deficits = intervalLabels.map((_, idx) => regionDeficit(dow, region, idx));
                            return (
                              <tr key={`region-summary-${region.id}-${dow}`}>
                                <td
                                  className="sticky left-0 z-10 px-4 py-1.5 font-bold text-white text-xs whitespace-nowrap"
                                  style={{ minWidth: STICKY_NAME_W, maxWidth: STICKY_NAME_W, backgroundColor: "#1E293B" }}
                                >
                                  {region.name} Total — {DOW_LABELS[dow]}
                                </td>
                                <td className="sticky z-10" style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, backgroundColor: "#1E293B" }} />
                                <td className="sticky z-10" style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W, backgroundColor: "#1E293B" }} />
                                <td className="sticky z-10" style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W, backgroundColor: "#1E293B" }} />
                                {deficits.map((d, idx) => {
                                  const bgColor   = d > 1 ? "#FEE2E2" : d > 0 ? "#FEF9C3" : d < 0 ? "#DCFCE7" : "#1E293B";
                                  const textColor = d > 1 ? "#B91C1C" : d > 0 ? "#92400E" : d < 0 ? "#166534" : "#FFFFFF";
                                  return (
                                    <td
                                      key={idx}
                                      className="py-1.5 text-center font-mono font-bold text-xs"
                                      style={{ minWidth: colW, backgroundColor: bgColor, color: textColor }}
                                    >
                                      {d !== 0 ? (d > 0 ? `+${d.toFixed(2)}` : d.toFixed(2)) : ""}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </>
                      );
                    })()}

                    {/* Spacer after region */}
                    <tr>
                      <td colSpan={4 + intervalLabels.length} className="h-2 bg-gray-200" />
                    </tr>
                  </React.Fragment>
                ))}

                {/* ── Overall summary — per-day totals across all regions ── */}
                {allRegions.length > 1 && (
                  <>
                    {/* Section divider: region blocks → overall totals */}
                    <tr>
                      <td
                        className="sticky left-0 z-10 px-4 py-1 text-[10px] font-bold uppercase tracking-widest border-t-2 border-slate-500"
                        style={{ minWidth: STICKY_NAME_W, backgroundColor: "#CBD5E1", color: "#334155" }}
                      >
                        All Regions — Daily Totals
                      </td>
                      <td className="sticky z-10 border-t-2 border-slate-500" style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, backgroundColor: "#CBD5E1" }} />
                      <td className="sticky z-10 border-t-2 border-slate-500" style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W, backgroundColor: "#CBD5E1" }} />
                      <td className="sticky z-10 border-t-2 border-slate-500" style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W, backgroundColor: "#CBD5E1" }} />
                      {intervalLabels.map((_, idx) => (
                        <td key={idx} className="border-t-2 border-slate-500" style={{ minWidth: colW, backgroundColor: "#CBD5E1" }} />
                      ))}
                    </tr>
                    <tr>
                      <td
                        className="sticky left-0 z-20 px-4 py-1.5 font-bold text-white text-xs tracking-wide uppercase"
                        style={{ minWidth: STICKY_NAME_W, width: STICKY_NAME_W, backgroundColor: "#1E3A5F" }}
                      >
                        Overall Summary
                      </td>
                      <td className="sticky z-20" style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, backgroundColor: "#1E3A5F" }} />
                      <td className="sticky z-20" style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W, backgroundColor: "#1E3A5F" }} />
                      <td className="sticky z-20" style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W, backgroundColor: "#1E3A5F" }} />
                      {intervalLabels.map((_, idx) => (
                        <td key={idx} style={{ minWidth: colW, backgroundColor: "#1E3A5F" }} />
                      ))}
                    </tr>
                    {WEEKDAYS.map((dow) => {
                      const deficits = intervalLabels.map((_, idx) => overallDeficit(dow, idx));
                      return (
                        <tr key={`overall-${dow}`}>
                          <td
                            className="sticky left-0 z-10 px-4 py-1.5 font-bold text-white text-xs whitespace-nowrap"
                            style={{ minWidth: STICKY_NAME_W, maxWidth: STICKY_NAME_W, backgroundColor: "#1E3A5F" }}
                          >
                            Overall — {DOW_LABELS[dow]}
                          </td>
                          <td className="sticky z-10" style={{ left: STICKY_NAME_W, minWidth: STICKY_SCHED_W, backgroundColor: "#1E3A5F" }} />
                          <td className="sticky z-10" style={{ left: STICKY_AHT_LEFT, minWidth: STICKY_AHT_W, backgroundColor: "#1E3A5F" }} />
                          <td className="sticky z-10" style={{ left: STICKY_CR_LEFT, minWidth: STICKY_CR_W, backgroundColor: "#1E3A5F" }} />
                          {deficits.map((d, idx) => {
                            const bgColor   = d > 1 ? "#FEE2E2" : d > 0 ? "#FEF9C3" : d < 0 ? "#DCFCE7" : "#1E3A5F";
                            const textColor = d > 1 ? "#B91C1C" : d > 0 ? "#92400E" : d < 0 ? "#166534" : "#FFFFFF";
                            return (
                              <td
                                key={idx}
                                className="py-1.5 text-center font-mono font-bold text-xs"
                                style={{ minWidth: colW, backgroundColor: bgColor, color: textColor }}
                              >
                                {d !== 0 ? (d > 0 ? `+${d.toFixed(2)}` : d.toFixed(2)) : ""}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-gray-100 border border-gray-300" />
              Non-working interval
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-slate-800">2.50</span>
              PCCs needed (actual)
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-mono" style={{ color: "#64748b" }}>~2.50</span>
              PCCs needed (projected)
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-indigo-600">0.75</span>
              PCC utilization
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-mono font-semibold text-xs" style={{ color: "#92400E", backgroundColor: "#FEF9C3", padding: "0 3px", borderRadius: 3 }}>0.5</span>
              <span style={{ color: "#92400E" }}>Lunch break</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: "#FEE2E2", border: "1px solid #FCA5A5" }} />
              <span style={{ color: "#B91C1C" }}>Understaffed (deficit &gt; 1)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: "#FEF9C3", border: "1px solid #FDE047" }} />
              <span style={{ color: "#92400E" }}>Borderline (0 – 1)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: "#DCFCE7", border: "1px solid #86EFAC" }} />
              <span style={{ color: "#166534" }}>Surplus (deficit &lt; 0)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
